import React, { useMemo, useState } from 'react';
import { Card, Typography, Input, Row, Col, Space, Tag, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import StandardTable from '../../../../components/common/StandardTable';
import { useGetQaqcPipeWeldingdataLog } from '../../../../hooks/genericHooks';
import type { QaqcPipeWeldingdataLog } from '../../../../types';
import VirtualList from 'rc-virtual-list';
import Button from '../../../../components/common/Button';

const { Title } = Typography;

const QaqcPipeWeldingDataLogList: React.FC = () => {
  const { data, isLoading } = useGetQaqcPipeWeldingdataLog();
  const list: QaqcPipeWeldingdataLog[] = data?.data ?? [];

  const [search, setSearch] = useState('');
  const [uniqueness, setUniqueness] = useState<'all' | 'unique' | 'non-unique'>('all');
  const [filters, setFilters] = useState<{
    fluid?: string[];
    section?: string[]; // area
    line_no?: string[];
    spool_id?: string[];
    revision_no?: string[];
    weld_category?: string[];
    pipesize_name?: string[];
    serviceclass_name?: string[];
    weldtype_name?: string[];
    invalidity?: string[];
    code_no?: string[];
    remarks?: string[];
  }>({});

  const cascadeOrder: (keyof QaqcPipeWeldingdataLog)[] = [
    'fluid',
    'section',
    'line_no',
    'spool_id',
    'revision_no',
    'weld_category',
    'pipesize_name',
    'serviceclass_name',
    'weldtype_name',
    'invalidity',
    'code_no',
    'remarks',
  ];

  // Virtualized tbody wrapper using rc-virtual-list
  const itemHeight = 40;
  const viewportHeight = 600;
  const VirtualBody = (rawData: any[], { children }: any) => {
    // children is an array of <tr> elements corresponding to rows
    const rows = Array.isArray(children) ? children : (children ? [children] : []);
    return (
      <VirtualList
        data={rows}
        height={viewportHeight}
        itemHeight={itemHeight}
        itemKey={(child: any) => (child?.key as React.Key)}
        component="tbody"
      >
        {(child: any) => child}
      </VirtualList>
    );
  };

  const applyPartialFilters = (
    dataIn: QaqcPipeWeldingdataLog[],
    opts?: { excludeField?: keyof QaqcPipeWeldingdataLog }
  ) => {
    return dataIn.filter(row => {
      for (const k of cascadeOrder) {
        if (opts?.excludeField === k) continue;
        const vals = (filters as any)[k] as string[] | undefined;
        if (vals && vals.length > 0) {
          const cell = String((row as any)[k] ?? '').trim();
          const cellLower = cell.toLowerCase();
          const matched = k === 'line_no'
            ? vals.some(v => String(v).toLowerCase().trim() !== '' && cellLower.includes(String(v).toLowerCase()))
            : vals.some(v => String(v).trim().toLowerCase() === cellLower);
          if (!matched) return false;
        }
      }
      return true;
    });
  };

  // Precompute per-weldingdata_id counts once per fetch and attach as a virtual field
  const listWithCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of list) {
      const key = String((r as any).weldingdata_id ?? '');
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return list.map(r => ({ ...(r as any), _wdCount: counts.get(String((r as any).weldingdata_id ?? '')) || 0 }));
  }, [list]);

  const buildOptions = (field: keyof QaqcPipeWeldingdataLog) => {
    const s = search.trim().toLowerCase();
    const searchFiltered = s
      ? (listWithCounts as any[]).filter(r => {
          const hay = `${r.weldingdata_id} ${r.line_no}`.toLowerCase();
          return hay.includes(s);
        })
      : (listWithCounts as any[]);
    // Apply other filters (excluding this field), then apply uniqueness constraint to narrow option sets
    const withFilters = applyPartialFilters(searchFiltered, { excludeField: field });
    const base = (() => {
      if (uniqueness === 'all') return withFilters;
      const wantUnique = uniqueness === 'unique';
      return withFilters.filter((r: any) => (wantUnique ? r._wdCount === 1 : r._wdCount > 1));
    })();
    const set = new Set<string>();
    base.forEach(r => {
      const v = (r as any)[field];
      const s = v === null || v === undefined ? '' : String(v).trim();
      if (s !== '') set.add(s);
    });
    return Array.from(set).sort((a,b) => a.localeCompare(b)).map(v => ({ label: v, value: v }));
  };

  const filteredData = useMemo(() => {
    const s = search.trim().toLowerCase();
    const byFilters = applyPartialFilters(listWithCounts as any);
    // Apply uniqueness filter on weldingdata_id over the filtered set
    let base = byFilters;
    if (uniqueness !== 'all') {
      const wantUnique = uniqueness === 'unique';
      base = byFilters.filter((r: any) => (wantUnique ? r._wdCount === 1 : r._wdCount > 1));
    }
    if (!s) return base;
    return base.filter((r: any) => {
      const hay = `${r.weldingdata_id} ${r.line_no} ${r.remarks ?? ''}`.toLowerCase();
      return hay.includes(s);
    });
  }, [listWithCounts, search, filters, uniqueness]);

  const columns: ColumnsType<QaqcPipeWeldingdataLog> = [
    { title: 'Mod.Date', dataIndex: 'mod_date', key: 'mod_date', width: 110, render: (v: any) => {
      if (!v) return '-';
      // 1) Try native Date
      const d = new Date(v);
      if (!isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}${m}${day}`;
      }
      const s = String(v);
      // 2) Try ISO date within string
      const iso = s.match(/\d{4}-\d{2}-\d{2}/);
      if (iso) return iso[0].replace(/-/g, '');
      // 3) Try RFC-like: 'Wed, 25 Jul 2018 00:00:00 GM(T)'
      const rfc = s.match(/\b(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\b/);
      if (rfc) {
        const [, ddStr, monStr, yyyyStr] = rfc;
        const months: Record<string, string> = {
          jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
          jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
        };
        const mm = months[monStr.toLowerCase()] ?? '01';
        const dd = String(parseInt(ddStr, 10)).padStart(2, '0');
        return `${yyyyStr}${mm}${dd}`;
      }
      // 4) Fallback: digits only
      const digits = s.replace(/[^0-9]/g, '');
      return digits.length >= 8 ? digits.slice(0, 8) : digits || '-';
    } },
    { title: 'Welding Data ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width:220},
    { title: 'Line No', dataIndex: 'line_no', key: 'line_no' },
    { title: 'Section', dataIndex: 'section', key: 'section', width: 80 },
    { title: 'Spool', dataIndex: 'spool_id', key: 'spool_id', width: 80 },
    { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 70},
    { title: 'Rev', dataIndex: 'revision_no', key: 'revision_no', width: 60 },
    { title: 'Weld No', dataIndex: 'weld_no', key: 'weld_no', width: 90 },
    { title: 'Cat', dataIndex: 'weld_category', key: 'weld_category', width: 50 },
    { title: 'Size', dataIndex: 'pipesize_name', key: 'pipesize_name', width: 70 },
    { title: 'W.Type', dataIndex: 'weldtype_name', key: 'weldtype_name', width: 80 },
    { title: 'S.Class', dataIndex: 'serviceclass_name', key: 'serviceclass_name', width: 90 },
    { title: 'Invalid', dataIndex: 'invalidity', key: 'invalidity', width: 60, render: (v) => (v ? <Tag color="red">{v}</Tag> : '-') },
    { title: 'Code', dataIndex: 'code_no', key: 'code_no', width: 70},
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
    { title: 'Weld Rev', dataIndex: 'weld_no_rev', key: 'weld_no_rev', width: 80 },
  ];

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col><Title level={4} style={{ margin: 0 }}>QA/QC Piping • Welding Data Log</Title></Col>
        <Col>
          <Space>
            <Input
              allowClear
              placeholder="Search id/line/remarks"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 360 }}
            />
            <Button size="small" onClick={() => { setFilters({}); setSearch(''); setUniqueness('all'); }}>Reset</Button>
          </Space>
        </Col>
      </Row>

      <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }} size={8}>
        <Row gutter={[8,8]}>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('fluid')}
              placeholder="Fluid"
              value={filters.fluid}
              onChange={(v) => setFilters(prev => ({ ...prev, fluid: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={3}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('section')}
              placeholder="Area (Section)"
              value={filters.section}
              onChange={(v) => setFilters(prev => ({ ...prev, section: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={4}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('line_no')}
              placeholder="Line No"
              value={filters.line_no}
              onChange={(v) => setFilters(prev => ({ ...prev, line_no: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (String(option?.label ?? '').toLowerCase()).startsWith(input.toLowerCase())
              }
              options={buildOptions('spool_id')}
              placeholder="Spool"
              value={filters.spool_id}
              onChange={(v) => setFilters(prev => ({ ...prev, spool_id: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('revision_no')}
              placeholder="Rev"
              value={filters.revision_no}
              onChange={(v) => setFilters(prev => ({ ...prev, revision_no: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('weld_category')}
              placeholder="W.Cat"
              value={filters.weld_category}
              onChange={(v) => setFilters(prev => ({ ...prev, weld_category: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('pipesize_name')}
              placeholder="Pipe Size"
              value={filters.pipesize_name}
              onChange={(v) => setFilters(prev => ({ ...prev, pipesize_name: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('serviceclass_name')}
              placeholder="S.Class"
              value={filters.serviceclass_name}
              onChange={(v) => setFilters(prev => ({ ...prev, serviceclass_name: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('invalidity')}
              placeholder="Invalid"
              value={filters.invalidity}
              onChange={(v) => setFilters(prev => ({ ...prev, invalidity: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('weldtype_name')}
              placeholder="W.Type"
              value={filters.weldtype_name}
              onChange={(v) => setFilters(prev => ({ ...prev, weldtype_name: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={buildOptions('code_no')}
              placeholder="Code"
              value={filters.code_no}
              onChange={(v) => setFilters(prev => ({ ...prev, code_no: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={3}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('remarks')}
              placeholder="Remarks"
              value={filters.remarks}
              onChange={(v) => setFilters(prev => ({ ...prev, remarks: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={3}>
            <Select
              options={[
                { label: 'All', value: 'all' },
                { label: 'Unique', value: 'unique' },
                { label: 'Non-unique', value: 'non-unique' },
              ]}
              placeholder="Uniqueness"
              value={uniqueness}
              onChange={(v) => setUniqueness(v)}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Space>

      <StandardTable<QaqcPipeWeldingdataLog>
        loading={isLoading}
        dataSource={filteredData}
        columns={columns as any}
        rowKey="id"
        bordered
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] }}
        components={{ body: VirtualBody as any }}
      />
    </Card>
  );
};

export default QaqcPipeWeldingDataLogList;
